import { describeColor } from './color'

/**
 * Merchandise artwork in a registered color: the heart, heart pair,
 * starbrush, and world map designs plus plain poster and canvas swatches.
 * Templates in public/merch/ use #123456 for the color and `white` for the
 * artwork; this fills both. Output sizes are print sizes.
 */

export type MerchId = 'poster' | 'canvas' | 'heart' | 'hearts' | 'starbrush' | 'worldmap'

export interface MerchProduct {
  id: MerchId
  title: string
  blurb: string
  /** Template URL under public/, or null for a plain swatch. */
  template: string | null
  /** Template or swatch size in SVG units. */
  width: number
  height: number
  /** Pixel width of the print-size raster. */
  printWidth: number
  /** Print dpi, which with printWidth gives the physical size. */
  dpi: number
}

export const MERCH: MerchProduct[] = [
  { id: 'poster', title: 'Poster', blurb: '18 × 24 in, your color with its name', template: null, width: 1800, height: 2400, printWidth: 5400, dpi: 300 },
  { id: 'canvas', title: 'Canvas', blurb: '15 × 15 in, your color edge to edge', template: null, width: 1500, height: 1500, printWidth: 4500, dpi: 300 },
  { id: 'heart', title: 'Heart', blurb: '12 × 12 in', template: '/merch/heartlove.svg', width: 432, height: 432, printWidth: 3600, dpi: 300 },
  { id: 'hearts', title: 'Heart Pair', blurb: '10 × 12 in', template: '/merch/heartpair.svg', width: 496, height: 600, printWidth: 3000, dpi: 300 },
  { id: 'starbrush', title: 'Starbrush', blurb: '10 × 10.5 in', template: '/merch/starbrush.svg', width: 2934, height: 3100, printWidth: 3000, dpi: 300 },
  { id: 'worldmap', title: 'World Map', blurb: '24 × 11 in', template: '/merch/worldmap.svg', width: 468, height: 210, printWidth: 7200, dpi: 300 },
]

export function merchById(id: string): MerchProduct | undefined {
  return MERCH.find((m) => m.id === id)
}

export interface MerchColor {
  name: string
  hex: string
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string)
}

function contrastHex(hex: string): string {
  return describeColor(hex).contrast === 'black' ? '#111111' : '#ffffff'
}

/** Fill a template: the placeholder color becomes the hex, the white artwork becomes the contrast color. */
export function fillMerch(template: string, color: MerchColor): string {
  const v = describeColor(color.hex)
  return template
    .replace(/#123456/gi, v.hex)
    .replace(/fill="white"/g, `fill="${contrastHex(v.hex)}"`)
    .replace(/fill:\s*white/g, `fill:${contrastHex(v.hex)}`)
    .replace(/IN_COLOR_NAME/g, esc(color.name))
    .replace(/IN_COLOR_HEX/g, v.hex)
}

/** A plain swatch with the name and hex, for posters and canvases. `fontCss` embeds fonts for downloads. */
export function swatchSvg(color: MerchColor, width: number, height: number, opts: { label?: boolean; fontCss?: string } = {}): string {
  const v = describeColor(color.hex)
  const fg = contrastHex(v.hex)
  const label = opts.label ?? true
  const size = Math.round(Math.min(width / 12, (width * 1.6) / Math.max(color.name.length, 8)))
  const style = opts.fontCss ? `<style>${opts.fontCss}</style>` : ''
  const text = label
    ? `<text x="${width / 2}" y="${height / 2}" fill="${fg}" font-family="'Jost Variable', Jost, Futura, 'Century Gothic', sans-serif" font-weight="400" font-size="${size}" text-anchor="middle" dominant-baseline="middle">${esc(color.name)}<tspan font-size="${Math.round(size * 0.32)}" baseline-shift="super">CM</tspan></text>
       <text x="${width / 2}" y="${height / 2 + size * 1.1}" fill="${fg}" font-family="Poppins, 'Helvetica Neue', Arial, sans-serif" font-weight="300" font-size="${Math.round(size * 0.38)}" text-anchor="middle" dominant-baseline="middle" opacity="0.85">${v.hex}</text>`
    : ''
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${style}<rect width="${width}" height="${height}" fill="${v.hex}"/>${text}</svg>`
}
